import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/lib/branding";
import { niceName } from "@/lib/format";

const STEPS = [
  ["O que fazer", "Aqui aparece, por processo, a coisa mais importante para hoje. Toque no processo para ver o resto do que há a tratar."],
  ["Um processo", "Cada cartão abre o processo: o «A tratar» diz o que falta, e as Partes mostram quem são o proprietário e o comprador."],
  ["Lembrar, com pré-visualização", "«Lembrar …» abre sempre a mensagem antes de enviar: vê o texto, o canal e o destinatário, pode editar e só depois envia."],
  ["Página do cliente", "Cada cliente recebe um link para a sua página, onde envia os documentos. Vê aqui se já abriu e o que já enviou."],
];
export const tourKey = (uid) => `imodesk_tour_done_${uid}`;
export const replayTour = (uid) => { localStorage.removeItem(tourKey(uid)); window.dispatchEvent(new Event("imodesk:tour")); };

const Mini = ({ children }) => <div className="w-full max-w-[340px] mx-auto rounded-2xl bg-white shadow-xl border border-slate-100 p-5 text-left">{children}</div>;
const Row = ({ dot, where, what }) => (
  <div className="flex items-center gap-3 py-3 border-t border-slate-100 first:border-t-0">
    <span className={`w-3 h-3 rounded-full shrink-0 ${dot}`} />
    <div className="flex-1 min-w-0"><div className="text-xs text-slate-400">{where}</div><div className="text-[15px] text-slate-800 leading-snug">{what}</div></div>
    <span className="text-xs font-semibold border border-slate-300 rounded-lg px-3 py-2 whitespace-nowrap">Lembrar</span>
  </div>
);
const MINIS = [
  <Mini key="a">
    <div className="text-[11px] font-semibold tracking-[0.12em] text-slate-400 uppercase mb-1">Hoje</div>
    <Row dot="bg-red-600" where="Morada do imóvel · escritura em breve" what="Falta um documento do proprietário" />
    <Row dot="bg-amber-500" where="Outro imóvel seu" what="Faltam documentos para o CMI" />
  </Mini>,
  <Mini key="b">
    <div className="text-base font-semibold text-ec-green">Lembrar o proprietário</div>
    <div className="flex gap-1.5 mt-2.5"><span className="text-xs font-semibold px-2.5 py-0.5 rounded-full border border-green-600 bg-green-50 text-green-800">WhatsApp</span><span className="text-xs px-2.5 py-0.5 rounded-full border border-slate-200 text-slate-500">Email</span></div>
    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 leading-relaxed">Olá. Ainda falta um documento. Pode enviá-lo por aqui: [link da sua página]</div>
    <div className="flex justify-end gap-2 mt-3"><span className="text-sm border border-slate-300 rounded-lg px-3 py-1.5">Editar</span><span className="text-sm bg-ec-green text-white rounded-lg px-3 py-1.5">Enviar</span></div>
  </Mini>,
  <Mini key="c">
    <div className="text-base font-semibold navy-text">Página do cliente</div>
    <div className="text-sm text-slate-500">Falta 1 documento</div>
    <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-4 text-center"><div className="text-sm text-slate-700">Cartão de cidadão</div><div className="mt-2 inline-block text-sm bg-ec-green text-white rounded-lg px-4 py-1.5">Tirar fotografia</div></div>
  </Mini>,
];
const WELCOME = [
  ["O que fazer", "Aqui aparece, por ordem, o que precisa de si."],
  ["Lembrar sem escrever", "Um toque prepara a mensagem; vê-a antes de enviar."],
  ["O cliente envia sozinho", "O cliente recebe um link e envia os documentos pelo telemóvel."],
];

// Telemóvel: 3 ecrãs de boas-vindas em ecrã inteiro (deslizar, pontos, Saltar/Começar).
function MobileWelcome({ done }) {
  const [i, setI] = useState(0);
  const x0 = useRef(null);
  const { user } = useAuth();
  const brand = useBranding();
  const last = i === WELCOME.length - 1;
  const onEnd = (e) => {
    if (x0.current == null) return;
    const dx = e.changedTouches[0].clientX - x0.current;
    if (dx < -40 && !last) setI(i + 1);
    if (dx > 40 && i > 0) setI(i - 1);
    x0.current = null;
  };
  return (
    <div className="fixed inset-0 z-[70] bg-[#F4F1EA] flex flex-col" data-testid="mobile-welcome"
      onTouchStart={(e) => { x0.current = e.touches[0].clientX; }} onTouchEnd={onEnd}>
      <div className="flex items-center justify-between px-5 pt-4 h-14 shrink-0">
        {i === 0 ? (brand?.logo ? <img src={brand.logo} alt={brand.name} className="h-9 max-w-[140px] object-contain" data-testid="welcome-logo" />
          : <span className="font-heading text-lg navy-text" data-testid="welcome-logo">{brand?.name}</span>) : <span />}
        <button onClick={done} className="text-sm font-semibold text-slate-500 px-2 py-1" data-testid="welcome-skip">Saltar</button>
      </div>
      {i === 0 && <div className="px-7 pt-1 text-lg font-semibold navy-text shrink-0" data-testid="welcome-hello">{user?.welcome || "Bem-vindo"} ao ImoDesk, {niceName(user?.name)?.split(" ")[0]}</div>}
      <div className="flex-[0_0_52%] flex items-center px-6" data-testid={`welcome-slide-${i}`}>{MINIS[i]}</div>
      <div className="flex-1 px-7 pt-1">
        <h1 className="font-heading text-3xl navy-text" data-testid="welcome-title">{WELCOME[i][0]}</h1>
        <p className="text-slate-600 text-base mt-2 leading-relaxed">{WELCOME[i][1]}</p>
      </div>
      <div className="p-6 flex items-center justify-between shrink-0">
        <div className="flex gap-2" data-testid="welcome-dots">{WELCOME.map((_, k) => <button key={k} onClick={() => setI(k)} aria-label={`Ecrã ${k + 1}`} className={`h-2 rounded-full transition-[width,background-color] ${k === i ? "w-6 bg-slate-800" : "w-2 bg-slate-300"}`} />)}</div>
        <button onClick={() => (last ? done() : setI(i + 1))} className="h-11 px-6 rounded-xl text-sm font-semibold navy-bg text-white" data-testid="welcome-next">{last ? "Começar" : "Seguinte"}</button>
      </div>
    </div>
  );
}

// Primeira entrada (por utilizador, neste navegador): computador → barra de 4 passos; telemóvel → 3 ecrãs de boas-vindas.
export function FirstTour({ uid }) {
  const key = tourKey(uid);
  const [i, setI] = useState(() => (uid && !localStorage.getItem(key) ? 0 : null));
  const [mobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const on = () => setI(0);
    window.addEventListener("imodesk:tour", on);
    return () => window.removeEventListener("imodesk:tour", on);
  }, []);
  const done = () => { localStorage.setItem(key, "1"); setI(null); window.dispatchEvent(new Event("imodesk:tour-done")); };
  if (i === null) return null;
  if (mobile) return <MobileWelcome done={done} />;
  const [title, text] = STEPS[i];
  return (
    <div className="fixed inset-x-3 bottom-[84px] md:inset-x-auto md:right-6 md:bottom-6 md:w-[620px] z-[60] navy-bg text-white rounded-2xl shadow-2xl px-5 py-4 flex flex-wrap md:flex-nowrap items-center gap-4" data-testid="first-tour">
      <div className="flex-1 min-w-[220px]">
        <div className="font-semibold" data-testid="tour-title"><span data-testid="tour-step">{i + 1} de {STEPS.length}</span> · {title}</div>
        <div className="text-sm text-white/80 mt-0.5">{text}</div>
      </div>
      <div className="flex items-center gap-1.5" aria-hidden>{STEPS.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${k === i ? "bg-white" : "bg-white/35"}`} />)}</div>
      <button onClick={done} className="h-9 px-3.5 rounded-lg text-sm font-semibold border border-white/40 hover:bg-white/10" data-testid="tour-skip">Saltar</button>
      <button onClick={() => (i + 1 < STEPS.length ? setI(i + 1) : done())} className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-white text-slate-900 hover:bg-slate-100" data-testid="tour-next">
        {i + 1 < STEPS.length ? "Seguinte" : "Começar"}
      </button>
    </div>
  );
}
